// AutomaticBoard.tsx
// Automatic/transactional push notification configuration

import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Webhook } from "lucide-react";
import { trpc } from "~/api/trpc";
import { PhonePreviewIOS, PhonePreviewAndroid } from "~/components/pages/PhonePreview";
import { asHttpImageOrNull } from "~/components/pages/utils";
import { TransactionalPushNotificationType, formatTransactionalPushNotificationType } from "~/types/server-types/TransactionalPushNotificationType";

interface AutomaticBoardProps {
  handleUpsertTransactionalConfig: (input: any) => Promise<any>;
}

function TransactionRow({
  type,
  label,
  templates,
  onFocus,
  handleUpsertTransactionalConfig,
}: {
  type: TransactionalPushNotificationType;
  label: string;
  templates: Array<{ 
    id: string; 
    name: string; 
    // Old fields (deprecated but may still exist)
    title?: string; 
    subtitle?: string | null; 
    body?: string; 
    // New platform-specific fields
    androidTitle: string;
    androidBody: string;
    iosTitle: string;
    iosSubtitle: string;
    iosBody: string;
    data?: Record<string, unknown>;
  }>;
  onFocus: (snapshot: { type: TransactionalPushNotificationType; templateId: string | null; enabled: boolean }) => void;
  handleUpsertTransactionalConfig: (input: {
    type: TransactionalPushNotificationType;
    enabled?: boolean;
    templateId?: string | null;
  }) => Promise<any>;
}) {
  const { data } = trpc.pushNotifications.getTransactionalPushNotificationConfig.useQuery({ type });

  const [localEnabled, setLocalEnabled] = React.useState<boolean>(!!data?.enabled);
  const [localTemplateId, setLocalTemplateId] = React.useState<string | null>(data?.templateId ?? null);

  React.useEffect(() => {
    setLocalEnabled(!!data?.enabled);
    setLocalTemplateId(data?.templateId ?? null);
  }, [data?.enabled, data?.templateId]);

  const handleToggle = async (checked: boolean) => {
    const prev = localEnabled;
    setLocalEnabled(checked);
    onFocus({ type, templateId: localTemplateId, enabled: checked });
    try {
      await handleUpsertTransactionalConfig({ type, enabled: checked, templateId: localTemplateId });
    } catch (e: any) {
      setLocalEnabled(prev);
      onFocus({ type, templateId: localTemplateId, enabled: prev });
      alert(e?.message || "Failed to update status");
    }
  };

  const handleTemplateChange = async (newId: string | undefined) => {
    const prev = localTemplateId;
    const next = newId ?? null;
    setLocalTemplateId(next);
    onFocus({ type, templateId: next, enabled: localEnabled });
    try {
      await handleUpsertTransactionalConfig({ type, templateId: next, enabled: localEnabled });
    } catch (e: any) {
      setLocalTemplateId(prev);
      onFocus({ type, templateId: prev, enabled: localEnabled });
      alert(e?.message || "Failed to link template");
    }
  };

  return (
    <div
      className="flex flex-col gap-3 p-3 hover:bg-muted/30 cursor-pointer rounded-xl"
      onClick={() => onFocus({ type, templateId: localTemplateId, enabled: localEnabled })}
      role="button"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium">{label}</div>
        </div>
        <div className="flex items-center gap-4 shrink-0" onClick={(e)=>e.stopPropagation()}>
          <div className="flex items-center gap-2">
            <Label className="text-xs">Enabled</Label>
            <Switch checked={localEnabled} onCheckedChange={handleToggle} />
          </div>
          <div className="w-60">
            <Select value={localTemplateId ?? undefined} onValueChange={handleTemplateChange}>
              <SelectTrigger className="rounded-xl"><SelectValue placeholder="Link template…" /></SelectTrigger>
              <SelectContent>
                {templates.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AutomaticBoard({
  handleUpsertTransactionalConfig,
}: AutomaticBoardProps) {
  const { data: templates } = trpc.pushNotifications.listPushNotificationTemplates.useQuery();
  const allTypes: TransactionalPushNotificationType[] = Object.values(TransactionalPushNotificationType) as any;

  const [focused, setFocused] = useState<{ type: TransactionalPushNotificationType; templateId: string | null; enabled: boolean } | null>(null);
  const selectedTemplate = (templates ?? []).find(t => t.id === focused?.templateId);
  const previewImageUrl = asHttpImageOrNull(selectedTemplate?.data?.image);
  const previewImage = previewImageUrl ? { name: "template-image", dataUrl: previewImageUrl } : null;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:auto-rows-min">
      <div className="lg:col-span-7 space-y-4">
        <Card className="rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Webhook className="h-4 w-4" /> Automatic — Transactions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <div className="divide-y rounded-xl border">
              {allTypes.map((t) => (
                <TransactionRow
                  key={t}
                  type={t}
                  label={formatTransactionalPushNotificationType(t)}
                  templates={templates ?? []}
                  onFocus={setFocused}
                  handleUpsertTransactionalConfig={handleUpsertTransactionalConfig}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="lg:col-span-5 lg:row-span-2 self-start">
        <Card className="rounded-2xl h-full">
          <CardHeader className="pb-2"><CardTitle className="text-base">Preview</CardTitle></CardHeader>
          <CardContent className="py-4">
            {selectedTemplate ? (
              <div className="space-y-4">
                <div className="text-xs text-muted-foreground text-center">Live Previews</div>
                
                {/* iOS Preview */}
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-blue-600 text-center">iOS</div>
                  <div className="flex justify-center">
                    <PhonePreviewIOS
                      appName="Your App"
                      header={selectedTemplate.iosTitle}
                      subtitle={selectedTemplate.iosSubtitle ?? undefined}
                      body={selectedTemplate.iosBody}
                      link={undefined}
                      image={previewImage}
                    />
                  </div>
                </div>

                {/* Android Preview */}
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-green-600 text-center">Android</div>
                  <div className="flex justify-center">
                    <PhonePreviewAndroid
                      appName="Your App"
                      title={selectedTemplate.androidTitle}
                      body={selectedTemplate.androidBody}
                      image={previewImage}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground text-center">Select a template in the list to preview.</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}